import Error from '@common/components/Error';
import { isMobile } from '@utils/grid';
import React, { FC } from 'react';
import NoSSR from 'react-no-ssr';
import ReactSelect, { MenuPlacement } from 'react-select';
import Creatable from 'react-select/creatable';
import { DefaultTheme } from 'styled-components';

import { Label } from '@common/components/Typography';
import InputWrapper from '../styled/FieldWrapper';

import { token } from 'odyssey';
import {
  FieldBlock,
  getSelectStyles,
  StyledMobileSelect,
  StyledMobileWrapper,
} from './BlockSelectField.styles';

export const getUnwrappedValue = (value) => (value && 'value' in value ? value.value : value);

export type PureSelectProps = {
  labelWidth?: string;
  requiredLabelWidth?: string | boolean;
  isRequired?: boolean;
  label?: string | JSX.Element;
  error?: string | JSX.Element | boolean;
  value?: {} | null;
  onChange: (...args: any[]) => any;
  placeholder?: string;
  handleAfterChange?: (...args: any[]) => any;
  getStyles?: (...args: any[]) => any;
  isDisabled?: boolean;
  options: any[];
  theme?: DefaultTheme;
  components?: {};
  renderMobileOption?: (...args: any[]) => any;
  useNativeDropdownOnMobile?: boolean;
  isCreatable?: boolean;
  id?: string;
  specialLabel?: JSX.Element;
  className?: string;
  fieldContainerClassName?: string;
  isSearchable?: boolean;
  menuPlacement?: MenuPlacement;
  noOptionsMessage?: (...args: any[]) => any;
  isMulti?: boolean;
  isClearable?: boolean;
};

export const PureSelect: FC<PureSelectProps> = ({
  isRequired = false,
  label,
  error,
  value,
  onChange,
  handleAfterChange = () => {},
  placeholder,
  theme,
  components,
  options,
  isDisabled = false,
  renderMobileOption,
  useNativeDropdownOnMobile = true,
  isCreatable,
  id,
  specialLabel,
  className,
  fieldContainerClassName,
  ...props
}) => {
  const DesktopComponent = isCreatable ? Creatable : ReactSelect;

  return (
    <FieldBlock className={className}>
      <InputWrapper>
        {(label || isRequired) && !specialLabel && (
          <Label error={error} isRequired={isRequired} htmlFor={id}>
            {label}
          </Label>
        )}
        {specialLabel}
        {useNativeDropdownOnMobile && isMobile() && options.length > 0 ? (
          <StyledMobileWrapper>
            <StyledMobileSelect
              disabled={isDisabled}
              value={getUnwrappedValue(value)}
              id={id}
              onChange={(event) => {
                // eslint-disable-next-line no-restricted-globals
                const selectValue = !isNaN(event.target.value as unknown as number)
                  ? parseInt(event.target.value, 10)
                  : event.target.value;

                const selectedOptionByValue = options.find(({ value }) => value === selectValue);
                const selectedOptionByLabel = options.find(({ label }) => label === selectValue);
                const selectedOption = selectedOptionByValue || selectedOptionByLabel;

                onChange(selectedOption);
                handleAfterChange(selectedOption);
              }}
            >
              <option value="" disabled selected hidden>
                {placeholder}
              </option>
              {options.map((option) => (
                <option
                  key={option.value}
                  disabled={option.isDisabled}
                  value={option.value}
                  selected={option.value === getUnwrappedValue(value)}
                >
                  {typeof renderMobileOption === 'function'
                    ? renderMobileOption(option)
                    : option.label}
                </option>
              ))}
            </StyledMobileSelect>
          </StyledMobileWrapper>
        ) : (
          <NoSSR>
            <DesktopComponent
              className={fieldContainerClassName}
              styles={getSelectStyles(theme, error)}
              value={value}
              onChange={(args) => {
                onChange(args);
                handleAfterChange(args);
              }}
              placeholder={placeholder}
              components={{
                ...components,
                DropdownIndicator: (props) => (
                  <>
                    <span
                      style={{
                        ...(props.getStyles('dropdownIndicator', props) as React.CSSProperties),
                        borderWidth: '6px 4px 0px',
                        borderColor: `${token('color-grey-3-neutral')} transparent transparent`,
                      }}
                      data-testid="dropdownIndicator"
                    />
                  </>
                ),
              }}
              options={options}
              isDisabled={isDisabled}
              inputId={id}
              {...{ ...props, error: !!error }}
            />
          </NoSSR>
        )}
      </InputWrapper>
      {error && <Error error={error} className="select-error" />}
    </FieldBlock>
  );
};
